import { Link } from '@/components/layout/TransitionLink'
import { ArrowUpRight, Disc3, Music2 } from 'lucide-react'
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from '@/components/ui/carousel'
import type { ThemeTile } from '@/hooks/useInstructorStats'
import type { PlaylistOptions } from '@/components/build/types'
import { buildUrl, pickRecipe } from '@/lib/buildLink'
import { formatPrecise } from '@/lib/format'
import { workoutNouns } from '@/lib/discipline'
import { SectionHeading } from '@/components/dashboard/fx/SectionHeading'

interface ThemeExplorerProps {
  instructorId: string
  themes: ThemeTile[]
  options?: PlaylistOptions | null
}

// A theme like "80s" can span an instructor's classes across more than
// one discipline, so "workouts" rather than "rides".
const { plural: WORKOUTS } = workoutNouns('all')

// This is the actual payoff of the whole app -- redundancy, essentials
// still missing, deep cuts -- all live one click into a theme. Big cover-art
// tiles read as "things to open" (plain pill tags never did).
export function ThemeExplorer({ instructorId, themes, options = null }: ThemeExplorerProps) {
  return (
    <Carousel opts={{ align: 'start', dragFree: true }} className="w-full">
      <div className="flex items-end justify-between gap-4">
        <SectionHeading
          index="02"
          title="Explore by"
          accent="theme."
          blurb="Parsed from class titles -- tap one to dig into the essentials still missing."
        />
        {/* Arrows are a desktop nicety -- touch devices already scrub the
            strip natively via drag. */}
        <div className="mb-10 hidden shrink-0 items-center gap-2 sm:flex">
          <CarouselPrevious className="static size-10 translate-x-0 translate-y-0" />
          <CarouselNext className="static size-10 translate-x-0 translate-y-0" />
        </div>
      </div>

      <CarouselContent className="-ml-4">
        {themes.map((theme) => {
          // Only offered once we know what they teach, and only for a theme
          // the builder has enough history to build from.
          const recipe = pickRecipe(options, theme.name)
          return (
          <CarouselItem key={theme.name} className="relative basis-auto pl-4">
            <Link
              to={`/instructors/${instructorId}/themes/${encodeURIComponent(theme.name)}`}
              className="group relative flex h-56 w-56 flex-col justify-end overflow-hidden rounded-3xl border border-border transition-all duration-500 hover:-translate-y-1.5 hover:border-primary/60 hover:shadow-2xl hover:shadow-primary/10 sm:h-64 sm:w-64"
            >
              {theme.image_url ? (
                <img
                  src={theme.image_url}
                  alt=""
                  loading="lazy"
                  className="absolute inset-0 size-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
              ) : (
                <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-primary/20 to-[#eb6834]/20 text-muted-foreground">
                  <Music2 className="size-10" />
                </div>
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/25 to-transparent" />

              <span className="absolute right-4 top-4 flex size-9 items-center justify-center rounded-full bg-white/10 text-white opacity-0 backdrop-blur transition-all duration-300 group-hover:opacity-100">
                <ArrowUpRight className="size-4" />
              </span>

              <div className="relative flex flex-col gap-1 p-5 text-white">
                <span className="truncate font-display text-2xl font-semibold capitalize tracking-tight">{theme.name}</span>
                <span className="text-xs text-white/70">
                  {formatPrecise(theme.count)} {WORKOUTS}
                </span>
              </div>
            </Link>
            {recipe?.theme && (
              // A sibling of the tile's link, not inside it (links can't nest).
              <Link
                to={buildUrl(instructorId, recipe)}
                aria-label={`Build a ${theme.name} ride`}
                className="absolute left-7 top-4 z-10 inline-flex items-center gap-1.5 rounded-full bg-black/55 px-3 py-1.5 text-xs font-medium text-white backdrop-blur transition-colors hover:bg-primary"
              >
                <Disc3 className="size-3.5" aria-hidden />
                Build a ride
              </Link>
            )}
          </CarouselItem>
          )
        })}
      </CarouselContent>
    </Carousel>
  )
}
